import React, { useEffect } from 'react';
import { BrowserRouter, Routes, Route, Navigate, useLocation } from 'react-router-dom';
import { UserProvider } from './contexts/UserContext';
import { StripeProvider } from './contexts/StripeContext';
import { FreeListenProvider } from './contexts/FreeListenContext';
import Layout from './components/layout/Layout';
import ProtectedRoute from './components/auth/ProtectedRoute';
import Home from './pages/Home';
import You from './pages/You';
import Meditation from './pages/Meditation';
import Popular from './pages/Popular';
import Guided from './pages/Guided';
import Unguided from './pages/Unguided';
import Search from './pages/Search';
import SetPassword from './pages/SetPassword';
import ResetPassword from './pages/ResetPassword';
import { ScriptCreationLayout } from './components/script-creation/ScriptCreationLayout';
import Plan from './pages/script-creation/Plan';
import Write from './pages/script-creation/Write';
import Speech from './pages/script-creation/Speech';
import Audio from './pages/script-creation/Audio';
import Publish from './pages/script-creation/Publish';
import ZenOptions from './pages/script-creation/ZenOptions';
import ZenPreview from './pages/script-creation/ZenPreview';
import ZenSummary from './pages/script-creation/ZenSummary';
import Featured from './pages/Featured';
import Following from './pages/Following';
import New from './pages/New';
import TOS from './pages/TOS';
import Privacy from './pages/Privacy';
import FAQ from './pages/FAQ';
import Profile from './pages/Profile';
import Subscription from './pages/Subscription';
import Blog from './pages/Blog';
import BlogPost from './pages/BlogPost';
import BlogEditor from './pages/BlogEditor';
import CookieBanner from './components/common/CookieBanner';
import RouteGuard from './components/common/RouteGuard';
import { setCouponCookie } from './utils/cookieManager';
import { config } from './config';

/**
 * CouponHandler - Detects coupon URL parameters on any page and saves to cookie
 * Runs on every route change to capture ?coupon=CODE or ?code=CODE from any URL
 */
const CouponHandler: React.FC = () => {
  const location = useLocation();

  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const couponParam = params.get('coupon') || params.get('code');

    if (couponParam) {
      const upperCode = couponParam.toUpperCase();
      setCouponCookie(upperCode);

      // Track the coupon click
      fetch(`${config.apiUrl}/coupons/track-click`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ code: upperCode })
      }).catch(err => console.error('Error tracking coupon click:', err));
    }
  }, [location.search]);

  return null; // This component doesn't render anything
};

/**
 * Main App Component - The root component of our React application
 * 
 * This component sets up the routing structure for our meditation app.
 * React Router allows us to create a single-page application (SPA) that 
 * behaves like a multi-page website by changing the URL and content 
 * without full page reloads.
 */
const App: React.FC = () => {
  return (
    // BrowserRouter: Enables routing functionality for the entire app
    // It uses the HTML5 history API to keep UI in sync with the URL
    <BrowserRouter>
      <CouponHandler />
      <UserProvider>
        <FreeListenProvider>
          <StripeProvider>
            <RouteGuard>
              <CookieBanner />
        {/* Routes: Container for all route definitions */}
      <Routes>
        {/* 
          Layout Route: This is a "wrapper" route that contains our header, sidebar, and footer
          All child routes will be rendered inside the Layout component
          This ensures consistent navigation and styling across all pages
        */}
        <Route element={<Layout />}>
          {/* Individual page routes - each URL path maps to a specific component */}
          <Route path="/" element={<Home />} />                    {/* Homepage */}
          <Route path="you" element={<ProtectedRoute><You /></ProtectedRoute>} />                   {/* User profile/dashboard */}
          <Route path="meditation/:id" element={<Meditation />} />         {/* View specific meditation (dynamic ID) */}
          <Route path="profile/:username" element={<Profile />} />         {/* View specific user profile (dynamic username) */}
          <Route path="popular" element={<Popular />} />           {/* Popular meditation scripts */}
          <Route path="guided" element={<Guided />} />             {/* Guided meditations */}
          <Route path="unguided" element={<Unguided />} />         {/* Unguided meditations */}
          <Route path="search" element={<Search />} />             {/* Search functionality */}
          <Route path="featured" element={<Featured />} />             {/* Featured meditations */}
          <Route path="following" element={<Following />} />             {/* Following meditations */}
          <Route path="new" element={<New />} />             {/* Newest meditations */}
          <Route path="subscription" element={<Subscription />} />              {/* Subscription plans */}
          <Route path="tos" element={<TOS />} />             {/* Terms of Service */}
          <Route path="privacy" element={<Privacy />} />             {/* Privacy Policy */}
          <Route path="faq" element={<FAQ />} />             {/* Frequently Asked Questions */}
          
          {/* Blog routes */}
          <Route path="blog" element={<Blog />} />             {/* Blog list */}
          <Route path="blog/:id/:slug" element={<BlogPost />} />             {/* Blog post view */}
          <Route path="blog/create" element={<ProtectedRoute requiresPermission="post_blog"><BlogEditor /></ProtectedRoute>} />             {/* Create blog post */}
          <Route path="blog/edit/:id" element={<ProtectedRoute requiresPermission="post_blog"><BlogEditor /></ProtectedRoute>} />             {/* Edit blog post */}
          
          {/* Script creation workflow */}
          <Route path="create/*" element={<ProtectedRoute><ScriptCreationLayout /></ProtectedRoute>}>
            <Route path="plan" element={<Plan />} />
            <Route path="write" element={<Write />} />
            <Route path="speech" element={<Speech />} />
            <Route path="audio" element={<Audio />} />
            <Route path="publish" element={<Publish />} />
            <Route path="zen" element={<ZenOptions />} />
            <Route path="zen-preview" element={<ZenPreview />} />
            <Route path="zen-summary" element={<ZenSummary />} />
            <Route index element={<Navigate to="plan" replace />} />
          </Route>

          {/* Edit existing script workflow */}
          <Route path="edit/:scriptId/*" element={<ProtectedRoute><ScriptCreationLayout /></ProtectedRoute>}>
            <Route path="plan" element={<Plan />} />
            <Route path="write" element={<Write />} />
            <Route path="speech" element={<Speech />} />
            <Route path="audio" element={<Audio />} />
            <Route path="publish" element={<Publish />} />
            <Route path="zen" element={<ZenOptions />} />
            <Route path="zen-preview" element={<ZenPreview />} />
            <Route path="zen-summary" element={<ZenSummary />} />
            <Route index element={<Navigate to="plan" replace />} />
          </Route>
          
          
        </Route>
        {/* Standalone routes (outside of Layout) */}
        <Route path="set-password" element={<SetPassword />} />    {/* Set password from welcome email */}
        <Route path="reset-password" element={<ResetPassword />} /> {/* Reset password from forgot password email */}
      </Routes>
          </RouteGuard>
        </StripeProvider>
        </FreeListenProvider>
      </UserProvider>
    </BrowserRouter>
  );
};

export default App; 